import {
  anchoredPos,
  moveEntityToBack,
  moveEntityToFront,
  getEntityToClick,
  getEntityToDrag,
  getHoverParentEntities,
  getZeroEntity,
  getZeroStaticEntity,
  setParent,
  getAllChildren,
  setEntityPosition,
} from './entity';
import { mouseDownThisFrame, mouseUpThisFrame, setVisibilityState } from './simulate';
import { defaultCursorSize, TEXT_STYLE } from './constants';
import { clamp01, isEqual, magnitudeSqr, subtractV2, v2 } from './helpers';
import { ObjectType } from './shared/api_shared_types';
import {
  Cursor,
  CursorStyle,
  DeltaTime,
  Entity,
  VisibilityState,
  GameManager,
  MoveMode,
  TextAppearStyle,
  Time,
  UserText,
  type GameObjectController,
} from './types';
import { changeTextObjectText, createAndAddLocalTextObject } from './textobject/textobject';
import { createAndAddLocalUserText } from './user_text';

import { updateTextEditorTransform } from './text_editor';
import { buttonStates } from './input';
import { setEntityHierarchyInteraction } from './interaction';
import logger from './controllers/logger';

export function controller(): GameObjectController {
  return {
    getNew: getZeroCursor,
    sync: null,
    update,
  };
}

export function getZeroCursor(ent: Entity): Cursor {
  let cursor: Cursor = {
    ...ent,

    objectType: ObjectType.CURSOR,

    allowHighlight: false,
    allowChildHighlight: false,

    textEditor: null,

    focussedThisMouseDown: false,

    enabled: true,
    prevPos: v2(),
    prevVel: [v2(), v2(), v2()],
    prevPosDt: [0, 0, 0],
    pos: v2(),
    vel: v2(),
    rot: 0,
    targetRot: 0,
    size: 30,
    text: '',
    placeholder: '',
    changed: false,
    userText: null,
    tObjPlaceholder: null,
    // tObjTooltip: null,
    // tooltip: '',
    // requestChangeTooltip: '',
    // requestHideTooltip: '',
    highlightEntities: [],
    highlightTopEntity: null,
    mouseDownOnEntity: null,
    mouseDownTime: 0,
    maybeDragEntity: null,
    draggedEntity: null,
    startDraggingEntityThisFrame: null,
    canceledDraggingEntityThisFrame: false,
    droppedEntityThisFrame: null,
    idleTime: 0,
    closestTextObject: null,
    closestPointOnTextObject: v2(),
    style: CursorStyle.colored,
    allowTyping: false,
    allowCreatingText: false,
    inputRequestTextObject: null,
    timeSinceLastTextTyped: 0,
    timeSinceLastTextInput: 0,
    inputRequestLineTransparancy: 255,
    inputRequestEntityCount: 0,
    clickToContinue: false,
    highlightCanvasses: [],
  };

  cursor.visibilityState = VisibilityState.DEFAULT;

  return cursor;
}

export function initCursor(cursor: Cursor, manager: GameManager) {
  cursor.size = defaultCursorSize;
  cursor.style = CursorStyle.simple_white;
  cursor.changed = true;
  cursor.userText = createCursorUserText(manager);
  setParent(cursor.userText, cursor, v2());
  moveEntityToBack(cursor.userText);
  cursor.userText.fixedZIndex = true;

  cursor.userText.moveMode = MoveMode.ANCHORED;
  cursor.userText.anchoredPos = anchoredPos(v2(0.5, 0.5), v2(22, 0), v2(1, 0.5));
  cursor.allowCreatingText = true;
  cursor.allowTyping = true;
  setParent(cursor, manager.space);

  const tObjPlaceHolder = createAndAddLocalTextObject(
    manager,
    getZeroStaticEntity(),
    '',
    TEXT_STYLE.CURSOR_PLACEHOLDER,
  );
  tObjPlaceHolder.moveMode = MoveMode.ANCHORED;
  tObjPlaceHolder.anchoredPos = anchoredPos(v2(0.5, 0.5), v2(22, 0), v2(1, 0.5));
  setVisibilityState(tObjPlaceHolder, VisibilityState.HIDDEN);

  setParent(tObjPlaceHolder, cursor);
  cursor.tObjPlaceholder = tObjPlaceHolder;
}

export function createCursorUserText(manager: GameManager): UserText {
  let t = createAndAddLocalUserText(
    manager,
    getZeroEntity(),
    '',
    true,
    TEXT_STYLE.USER_TEXT_CURSOR,
    0,
  );

  t.textObject.changeDuration = 0;
  t.dragged = false;
  t.moveMode = MoveMode.CURSOR;
  t.influenceKinetic = false;
  t.appearDuration = 0.15;
  t.textObject.appearDuration = 0.15;
  t.disappearDuration = 0;
  t.textObject.disappearDuration = 0;
  t.allowHighlight = false;
  t.clickable = false;
  t.allowDrag = false;
  t.pushBack = false;

  setVisibilityState(t, VisibilityState.APPEARING);

  return t;
}

export function setCursorPlaceholderText(cursor: Cursor, text: string) {
  cursor.placeholder = text;
  cursor.changed = true;
}

export function updateCursorPosition(cursor: Cursor, dt: DeltaTime) {
  //update idle time
  if (buttonStates.LEFT_MOUSE.isDown) {
    cursor.idleTime = 0;
  } else if (cursor.text.length === 0) {
    cursor.idleTime += dt;
  }
  cursor.timeSinceLastTextTyped += dt;
  cursor.timeSinceLastTextInput += dt;

  // Cursor movement
  {
    let mousePos = cursor.manager.mousePos;

    if (buttonStates.TOUCH.changedThisFrame) {
      cursor.prevPos.x = mousePos.x;
      cursor.prevPos.y = mousePos.y;

      cursor.prevVel[2].x = 0;
      cursor.prevVel[2].y = 0;

      cursor.prevVel[1].x = 0;
      cursor.prevVel[1].y = 0;
    } else {
      cursor.prevPos.x = cursor.pos.x;
      cursor.prevPos.y = cursor.pos.y;

      cursor.prevVel[2].x = cursor.prevVel[1].x;
      cursor.prevVel[2].y = cursor.prevVel[1].y;

      cursor.prevVel[1].x = cursor.prevVel[0].x;
      cursor.prevVel[1].y = cursor.prevVel[0].y;
    }

    if (!isEqual(cursor.anchoredPos.offset, mousePos)) {
      cursor.idleTime = 0;
      cursor.pos.x = mousePos.x;
      cursor.pos.y = mousePos.y;
      cursor.anchoredPos.offset.x = mousePos.x;
      cursor.anchoredPos.offset.y = mousePos.y;
    }

    cursor.prevVel[0].x = (cursor.pos.x - cursor.prevPos.x) / dt;
    cursor.prevVel[0].y = (cursor.pos.y - cursor.prevPos.y) / dt;

    cursor.vel.x = (cursor.prevVel[0].x + cursor.prevVel[1].x + cursor.prevVel[2].x) / 3;
    cursor.vel.y = (cursor.prevVel[0].y + cursor.prevVel[1].y + cursor.prevVel[2].y) / 3;
  }
}

export function updateCursorHighlighting(cursor: Cursor) {
  //Handle highlighting
  const manager = cursor.manager;

  if (cursor.enabled) {
    return;
  }

  const cursorPosCenteredBG = subtractV2(
    cursor.pos,
    v2(manager.spaceBG.width / 2, manager.spaceBG.height / 2),
  );

  setEntityHierarchyInteraction(manager.spaceBG, cursorPosCenteredBG, 1);

  const cursorPosCentered = subtractV2(
    cursor.pos,
    v2(manager.space.width / 2, manager.space.height / 2),
  );

  cursor.highlightCanvasses.length = 0;
  cursor.highlightTopEntity = setEntityHierarchyInteraction(manager.space, cursorPosCentered, 1);
  cursor.highlightEntities = manager.entities.filter((it) => it.highlighted);

  if (!cursor.draggedEntity && cursor.text.length === 0) {
    //&&
    // !( buttonStates.LEFT_MOUSE.isDown && !buttonStates.LEFT_MOUSE.changedThisFrame ) ) {
    for (let i = 0; i < cursor.highlightEntities.length; i++) {
      const ent = cursor.highlightEntities[i];
      ent.highlighted = false;
      ent.stopHighlightThisFrame = true;
      // ent.isDirty = true;
    }
    cursor.highlightEntities = [];

    let topEntity: Entity = null;
    if (manager.mainRoom) {
      topEntity = cursor.highlightTopEntity;
    }

    if (topEntity) {
      getHoverParentEntities(topEntity, cursor.highlightEntities);
      cursor.highlightEntities.forEach((it) => {
        it.highlighted = true;
        if (!it.highlightLastFrame) {
          it.startHighlightThisFrame = true;
          it.isDirty = true;
        }
        it.stopHighlightThisFrame = false;
      });
      // cursor.idleTime = 0;
    }
  }

  if (
    cursor.draggedEntity &&
    cursor.highlightEntities.length === 0 &&
    cursor.draggedEntity !== cursor.userText
  ) {
    cursor.draggedEntity.highlighted = true;
    cursor.highlightEntities.push(cursor.draggedEntity);
  }
}

export function updateCursorInteraction(cursor: Cursor, dt: DeltaTime) {
  const manager = cursor.manager;

  //Click & Dragging
  {
    if (mouseDownThisFrame()) {
      cursor.mouseDownTime = 0;

      if (cursor.highlightEntities.length > 0) {
        cursor.maybeDragEntity = getEntityToDrag(cursor.highlightEntities[0]);
        if (cursor.maybeDragEntity) {
          const dragParent = cursor.maybeDragEntity.parent;

          cursor.maybeDragEntity.dragPos = dragParent.relativeMousePos;
        }

        const entityToClick = getEntityToClick(cursor.highlightEntities[0]);

        if (entityToClick) {
          if (cursor.maybeDragEntity) {
            let mag = magnitudeSqr(cursor.vel);
            if (mag > 500000) {
              startDragEntity(cursor.maybeDragEntity, cursor);
            } else {
              entityToClick.mouseDownOnMe = true;
              entityToClick.mouseDownTime = 0;
              cursor.mouseDownOnEntity = entityToClick;
            }
          } else {
            entityToClick.mouseDownOnMe = true;
            entityToClick.mouseDownTime = 0;
            cursor.mouseDownOnEntity = entityToClick;
          }
          if (
            (!cursor.draggedEntity && !cursor.mouseDownOnEntity) ||
            cursor.mouseDownOnEntity?.passClickCanvas
          ) {
            if (manager.mainRoom?.canvas) {
              manager.mainRoom.canvas.mouseDownOnMe = true;
            }
          }
        } else if (cursor.maybeDragEntity) {
          //No entity to click, so start dragging entityToDrag right away
          startDragEntity(cursor.maybeDragEntity, cursor);
        }
      }
    } else if (buttonStates.LEFT_MOUSE.isDown) {
      cursor.mouseDownTime += dt;

      if (cursor.maybeDragEntity) {
        const stillHoveringEntity = cursor.highlightEntities.some(
          (it) => it === cursor.mouseDownOnEntity,
        );
        if (stillHoveringEntity) {
          let mag = magnitudeSqr(cursor.vel);
          if (mag > 500000 || cursor.mouseDownTime > 0.3) {
            startDragEntity(cursor.maybeDragEntity, cursor);
          }
        } else {
          startDragEntity(cursor.maybeDragEntity, cursor);
        }
      }
    } else if (mouseUpThisFrame()) {
      let wasDragging = cursor.draggedEntity;
      cursor.maybeDragEntity = null;
      stopDragEntity(cursor);

      if (!wasDragging && cursor.highlightEntities.some((it) => it === cursor.mouseDownOnEntity)) {
        if (cursor.mouseDownTime < 0.3) {
          const clickedEntity = cursor.mouseDownOnEntity;
          clickedEntity.clickedThisFrame = true;
          moveEntityToFront(clickedEntity);

          clickedEntity.mouseDownOnMe = false;
        }
      }
      //reset all mouse down on entities
      for (let i = 0; i < manager.entities.length; i++) {
        manager.entities[i].mouseDownOnMe = false;
      }
      cursor.mouseDownOnEntity = null;
    }
  }
}

export function update(cursor: Cursor): boolean {
  // if (cursor.requestChangeTooltip) {
  //   if (cursor.requestChangeTooltip !== cursor.tooltip) {
  //     setCursorToolTip(cursor, cursor.requestChangeTooltip);
  //   }
  //   cursor.requestChangeTooltip = '';
  //   cursor.requestHideTooltip = '';
  // } else if (cursor.requestHideTooltip) {
  //   if (cursor.requestHideTooltip === cursor.tooltip) {
  //     setCursorToolTip(cursor, '');
  //   }
  //   cursor.requestHideTooltip = '';
  // }

  if (cursor.userText && cursor.manager.mainRoom?.canvas) {
    cursor.scale = cursor.manager.mainRoom.canvas.layer.scale;
  }

  if (cursor.focussedThisMouseDown && mouseUpThisFrame()) {
    cursor.focussedThisMouseDown = false;
  }

  if (cursor.textEditor) {
    const editorText = cursor.textEditor.inputElement.value;
    const objectText = cursor.userText.textObject.text;
    if (editorText !== objectText) {
      changeTextObjectText(cursor.userText.textObject, editorText);
      updateTextEditorTransform(cursor.textEditor);
      cursor.text = editorText;
    }
  }

  // if (cursor.tObjTooltip) {
  //   //move tooltip to the left when cursor is close to the right border
  //   if (cursor.pos.x > cursor.manager.space.width - 150) {
  //     const x = 50 - 100 * clamp01((150 - (cursor.manager.space.width - cursor.pos.x)) / 100);
  //     setEntityPosition(cursor.tObjTooltip, x, 50);
  //   } else if (cursor.pos.x < cursor.manager.space.width - 150 && cursor.tObjTooltip.pos.x !== 50) {
  //     setEntityPosition(cursor.tObjTooltip, 50, 50);
  //   }
  // }

  return false;
}

export function updateCursorText(cursor: Cursor, time: Time) {
  cursor.idleTime = 0;

  cursor.changed = false;

  if (cursor.text.length > 0) {
    if (cursor.highlightEntities.length > 0) {
      cursor.highlightEntities = [];
    }
    setVisibilityState(cursor.tObjPlaceholder, VisibilityState.HIDDEN);
  } else if (cursor.placeholder) {
    changeTextObjectText(cursor.tObjPlaceholder, cursor.placeholder);
    setVisibilityState(cursor.tObjPlaceholder, VisibilityState.DEFAULT);
  }
}

// function setCursorToolTip(cursor: Cursor, text: string) {
//   cursor.tooltip = text;
//   if (cursor.tObjTooltip) {
//     if (text === '') {
//       setVisibilityState(cursor.tObjTooltip, VisibilityState.DISAPPEARING);
//       cursor.tObjTooltip = null;
//     } else {
//       changeTextObjectText(cursor.tObjTooltip, text);
//     }
//   } else {
//     let tObj = createAndAddLocalTextObject(
//       cursor.manager,
//       getZeroEntity(),
//       text,
//       TEXT_STYLE.CURSOR_TOOLTIP,
//       8,
//     );

//     tObj.appearDuration = 0.15;
//     tObj.disappearDuration = 0.1;
//     tObj.appearStyle = TextAppearStyle.FADE_AND_MOVE_DOWN;
//     tObj.onlyShowInDefaultState = false;
//     tObj.moveMode = MoveMode.STATIC;
//     tObj.allowDrag = false;
//     cursor.tObjTooltip = tObj;
//     setParent(tObj, cursor, v2(50, 50));
//   }
// }

function startDragEntity(entity: Entity, cursor: Cursor) {
  entity.dragged = true;
  if (entity.allowHighlight) {
    entity.highlighted = true;
  }

  const children = getAllChildren(entity);
  children.forEach((ent) => (ent.dragged = true));

  cursor.draggedEntity = entity;
  cursor.maybeDragEntity = null;
  moveEntityToFront(entity);

  cursor.startDraggingEntityThisFrame = entity;
}

export function stopDragEntity(cursor: Cursor) {
  if (!cursor.draggedEntity) return;

  let ent = cursor.draggedEntity;

  ent.dragged = false;
  ent.droppedThisFrame = true;
  cursor.droppedEntityThisFrame = ent;

  const children = getAllChildren(ent);
  children.forEach((ent) => (ent.dragged = false));

  cursor.draggedEntity = null;
}
